import type { Point } from "./types";

/* ---------------------------------------------------------------------------
 * Prize-collecting capacitated vehicle routing — the Cart & Run engine.
 *
 * A hero starts at a mine cart (the depot) in the middle of a dungeon room,
 * makes round trips out to scattered loot and back, banking what it carries
 * each time. Two limits bite at once: a per-trip carrying CAPACITY (you can
 * only haul so much before you must return to the cart) and a total STEP
 * BUDGET (every step lets the monster close in — when it arrives, anything not
 * yet in the cart is dropped). There is more loot than you can ever bank, so
 * the real question is *which* loot to take and how to route the trips.
 *
 * That is a real, named Operations Research problem: the prize-collecting
 * Capacitated Vehicle Routing Problem (a.k.a. the capacitated team
 * orienteering / profitable-tour problem). Not every customer must be served;
 * each carries a prize; a fleet of capacity-limited routes leaves and returns
 * to one depot; you maximize collected prize under a travel budget.
 *
 * We model it as a single-commodity-flow mixed-integer program in CPLEX LP
 * format (the format HiGHS reads). The flow variables both enforce per-trip
 * capacity *and* eliminate subtours, so a returned "OPTIMAL" is a proven-best
 * haul. The instances a game uses are small and handcrafted, so HiGHS proves
 * the optimum live in the browser.
 * ------------------------------------------------------------------------- */

/** One piece of loot sitting on a cell: a prize and a weight toward capacity. */
export interface LootNode {
  prize: number;
  weight: number;
}

export interface PrizeRoutingRequest {
  /**
   * Node coordinates. Index 0 is the cart (the depot — every trip starts and
   * ends here); indices 1..n are loot cells. Coordinates are grid cells, so
   * the natural distance is Manhattan (4-directional walking).
   */
  points: Point[];
  /** Loot for the non-depot nodes: `loot[k]` describes `points[k + 1]`. */
  loot: LootNode[];
  /** Per-trip carrying capacity (max total weight before returning to cart). */
  capacity: number;
  /** Total step budget — when it runs out, the monster arrives. */
  budget: number;
  /**
   * Optional explicit distance matrix (`dist[i][j]`). When omitted, integer
   * Manhattan distances between `points` are used (4-directional grid walking).
   */
  dist?: number[][];
  /** Upper bound on the number of trips (depot out-degree). Default: loot count. */
  maxTrips?: number;
  /** Solver wall-clock limit in seconds (default 30). */
  timeLimitSec?: number;
}

export interface PrizeRoutingResult {
  /** "OPTIMAL" only when the solver proved it; otherwise "FEASIBLE" / other. */
  status: string;
  /** Total prize banked (the objective). */
  prize: number;
  /** Total weight banked. */
  weight: number;
  /** Total steps walked across all trips. */
  steps: number;
  /** Each trip as a node sequence starting and ending at the cart: [0, …, 0]. */
  trips: number[][];
  /** Indices (into `points`) of the loot that ends up collected. */
  collected: number[];
  tBuildMs: number;
  /** Pure solver time, measured by the caller around the solve. */
  tSolveMs: number;
}

/** Integer Manhattan distance between two grid points. */
export function manhattan(a: Point, b: Point): number {
  return Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]);
}

/** Full integer Manhattan distance matrix for a list of grid points. */
export function manhattanMatrix(points: Point[]): number[][] {
  const n = points.length;
  const d: number[][] = Array.from({ length: n }, () => new Array(n).fill(0));
  for (let i = 0; i < n; i++)
    for (let j = i + 1; j < n; j++) {
      const v = manhattan(points[i], points[j]);
      d[i][j] = v;
      d[j][i] = v;
    }
  return d;
}

/** Resolve the distance matrix a request will use. */
export function routingMatrix(req: PrizeRoutingRequest): number[][] {
  return req.dist ?? manhattanMatrix(req.points);
}

/** Format a coefficient as a plain decimal (no scientific notation). */
function coef(v: number): string {
  return String(Number(v.toFixed(6)));
}

/** Precomputed structure for decoding a solved prize-routing model. */
export interface PrizeRoutingModel {
  lp: string;
  /** Total node count (depot + loot). */
  nodes: number;
  dist: number[][];
}

/**
 * Build the prize-collecting capacitated routing problem as a single-commodity
 * flow MILP in CPLEX LP format:
 *
 *   maximize    Σ_k prize_k y_k                              (loot banked)
 *   subject to  Σ_j x_kj = y_k,  Σ_i x_ik = y_k   (k ∈ loot)  (degree = visited)
 *               Σ_j x_0j − Σ_i x_i0 = 0                       (trips balance)
 *               [ Σ_j x_0j ≤ maxTrips ]                       (optional trip cap)
 *               Σ_j f_kj − Σ_{i≠0} f_ik = weight_k y_k  (k ∈ loot)  (collect)
 *               f_ij ≤ Q x_ij                  (i ∈ loot)     (per-trip capacity)
 *               Σ_{i≠j} d_ij x_ij ≤ B                         (step budget)
 *               x_ij, y_k ∈ {0,1},  f_ij ≥ 0
 *
 * The flow f is the load carried on each arc: the hero leaves the cart empty
 * (no flow on depot-out arcs), gains weight_k at each loot node, and the arc
 * returning to the cart carries the whole trip's load — bounded by Q. Because
 * any cycle disjoint from the depot would have to create weight from nowhere,
 * the same flow forbids subtours; no extra MTZ constraints are needed.
 */
export function buildPrizeRoutingLP(req: PrizeRoutingRequest): PrizeRoutingModel {
  const n = req.loot.length; // loot nodes 1..n
  const nodes = n + 1;
  const dist = routingMatrix(req);
  const Q = req.capacity;
  const B = req.budget;
  const maxTrips = req.maxTrips ?? n;
  const prize = (k: number) => req.loot[k - 1].prize;
  const weight = (k: number) => req.loot[k - 1].weight;

  let lp = "\\ Prize-collecting capacitated VRP - single-commodity-flow MILP (HiGHS)\n";
  lp += `\\ ${nodes} nodes (depot 0 + ${n} loot), capacity ${Q}, step budget ${B}\n`;

  // Objective: total prize of collected loot.
  const objTerms: string[] = [];
  for (let k = 1; k <= n; k++) objTerms.push(`${coef(prize(k))} y_${k}`);
  lp += "Maximize\n obj: " + (objTerms.length ? objTerms.join(" + ") : "0") + "\n";

  lp += "Subject To\n";

  // Degree: a loot node is entered and left exactly when it is collected.
  for (let k = 1; k <= n; k++) {
    const outs: string[] = [];
    for (let j = 0; j <= n; j++) if (j !== k) outs.push(`x_${k}_${j}`);
    lp += ` out_${k}: ${outs.join(" + ")} - y_${k} = 0\n`;
    const ins: string[] = [];
    for (let i = 0; i <= n; i++) if (i !== k) ins.push(`x_${i}_${k}`);
    lp += ` in_${k}: ${ins.join(" + ")} - y_${k} = 0\n`;
  }

  // Trips leave and return to the cart in equal number.
  {
    const outs: string[] = [];
    for (let j = 1; j <= n; j++) outs.push(`x_0_${j}`);
    const ins: string[] = [];
    for (let i = 1; i <= n; i++) ins.push(`x_${i}_0`);
    lp += ` depbal: ${outs.join(" + ")} - ${ins.join(" - ")} = 0\n`;
    lp += ` deptrips: ${outs.join(" + ")} <= ${maxTrips}\n`;
  }

  // Flow conservation (collection): out − in = weight picked up here. Depot-out
  // arcs carry no flow (the hero leaves the cart empty), so they are omitted.
  for (let k = 1; k <= n; k++) {
    const terms: string[] = [];
    for (let j = 0; j <= n; j++) if (j !== k) terms.push(`f_${k}_${j}`); // out (incl. to depot)
    for (let i = 1; i <= n; i++) if (i !== k) terms.push(`- f_${i}_${k}`); // in (loot only)
    lp += ` flow_${k}: ${terms.join(" + ").replace(/\+ -/g, "-")} - ${coef(weight(k))} y_${k} = 0\n`;
  }

  // Capacity: load on any arc out of a loot node is bounded by Q when used.
  for (let i = 1; i <= n; i++)
    for (let j = 0; j <= n; j++)
      if (j !== i) lp += ` cap_${i}_${j}: f_${i}_${j} - ${Q} x_${i}_${j} <= 0\n`;

  // Step budget: total walking across all trips.
  const budgetTerms: string[] = [];
  for (let i = 0; i <= n; i++)
    for (let j = 0; j <= n; j++)
      if (i !== j) budgetTerms.push(`${coef(dist[i][j])} x_${i}_${j}`);
  lp += ` budget: ${budgetTerms.join(" + ")} <= ${B}\n`;

  lp += "Bounds\n";
  for (let i = 1; i <= n; i++)
    for (let j = 0; j <= n; j++)
      if (j !== i) lp += ` 0 <= f_${i}_${j} <= ${Q}\n`;

  lp += "Binary\n";
  for (let i = 0; i <= n; i++)
    for (let j = 0; j <= n; j++)
      if (i !== j) lp += ` x_${i}_${j}\n`;
  for (let k = 1; k <= n; k++) lp += ` y_${k}\n`;

  lp += "End\n";

  return { lp, nodes, dist };
}

/**
 * Decode a solved column vector into trips (each `[0, …, 0]`). Follows the
 * chosen out-arc from every node; the depot may have several out-arcs (one per
 * trip). Returns null if the arcs don't form clean depot-rooted trips.
 */
export function decodeTrips(
  nodes: number,
  columnPrimal: (name: string) => number,
): number[][] | null {
  const out: Record<number, number[]> = {};
  for (let i = 0; i < nodes; i++) {
    out[i] = [];
    for (let j = 0; j < nodes; j++)
      if (i !== j && columnPrimal(`x_${i}_${j}`) > 0.5) out[i].push(j);
  }

  const trips: number[][] = [];
  const seen = new Array<boolean>(nodes).fill(false);
  for (const start of out[0]) {
    const trip = [0];
    let cur = start;
    let guard = 0;
    while (cur !== 0) {
      if (seen[cur]) return null; // a loot node reused across trips ⇒ malformed
      seen[cur] = true;
      trip.push(cur);
      const nexts = out[cur];
      if (nexts.length !== 1) return null; // each visited loot node has one out-arc
      cur = nexts[0];
      if (++guard > nodes + 1) return null;
    }
    trip.push(0);
    trips.push(trip);
  }
  return trips;
}

/** Total steps walked across a set of trips. */
export function tripsSteps(trips: number[][], dist: number[][]): number {
  let s = 0;
  for (const t of trips)
    for (let i = 0; i + 1 < t.length; i++) s += dist[t[i]][t[i + 1]];
  return s;
}

/** Summarize trips into the public result fields (prize / weight / collected). */
export function summarizeTrips(
  req: PrizeRoutingRequest,
  trips: number[][],
): { prize: number; weight: number; steps: number; collected: number[] } {
  const dist = routingMatrix(req);
  const collected: number[] = [];
  let prize = 0;
  let weight = 0;
  for (const t of trips)
    for (const node of t)
      if (node !== 0) {
        collected.push(node);
        prize += req.loot[node - 1].prize;
        weight += req.loot[node - 1].weight;
      }
  return { prize, weight, steps: tripsSteps(trips, dist), collected };
}

/**
 * Validate a set of trips against a request, returning a list of human-readable
 * violations (empty ⇒ a feasible haul). The interactive board uses this as a
 * referee so the rules a player can exploit match exactly what the MILP allows.
 */
export function prizeRoutingViolations(
  req: PrizeRoutingRequest,
  trips: number[][],
): string[] {
  const dist = routingMatrix(req);
  const v: string[] = [];
  const visited = new Set<number>();
  for (let ti = 0; ti < trips.length; ti++) {
    const t = trips[ti];
    if (t.length < 2 || t[0] !== 0 || t[t.length - 1] !== 0)
      v.push(`trip ${ti} must start and end at the cart`);
    let load = 0;
    for (let i = 1; i < t.length - 1; i++) {
      const node = t[i];
      if (node < 1 || node > req.loot.length) { v.push(`trip ${ti} visits a non-loot node`); continue; }
      if (visited.has(node)) v.push(`loot ${node} collected more than once`);
      visited.add(node);
      load += req.loot[node - 1].weight;
    }
    if (load > req.capacity) v.push(`trip ${ti} carries ${load} > capacity ${req.capacity}`);
  }
  const steps = tripsSteps(trips, dist);
  if (steps > req.budget) v.push(`total steps ${steps} > budget ${req.budget}`);
  return v;
}

/**
 * A fast, honest *heuristic* baseline — the kind of haul a player improvising
 * trip by trip would get. It is never claimed optimal; the reveal solver proves
 * the real best. Used to design levels where the obvious play falls short, and
 * available as a comparison/sanity check.
 *
 * Builds trips one at a time. Within a trip it walks, from wherever it stands,
 * to the loot maximizing `prize / (stepsToReach + 1)` among the loot that still
 * fits the carrying capacity *and* leaves enough budget to get back to the cart.
 * `mode: "value"` instead grabs the highest-prize reachable loot. The better of
 * the two runs is returned.
 */
export function greedyPrizeRouting(req: PrizeRoutingRequest): PrizeRoutingResult {
  const t0 = Date.now();
  const best = ["ratio", "value"].map((mode) =>
    greedyRun(req, mode as "ratio" | "value"),
  ).reduce((a, b) => (b.prize > a.prize ? b : a));
  return { ...best, tBuildMs: Date.now() - t0, tSolveMs: 0 };
}

function greedyRun(
  req: PrizeRoutingRequest,
  mode: "ratio" | "value",
): Omit<PrizeRoutingResult, "tBuildMs" | "tSolveMs"> {
  const dist = routingMatrix(req);
  const n = req.loot.length;
  const Q = req.capacity;
  const B = req.budget;
  const uncollected = new Set<number>();
  for (let k = 1; k <= n; k++) if (req.loot[k - 1].weight <= Q) uncollected.add(k);

  const trips: number[][] = [];
  let spent = 0;

  for (;;) {
    let cur = 0;
    let load = 0;
    let tripSteps = 0;
    const trip = [0];
    for (;;) {
      let pick = -1;
      let pickScore = -Infinity;
      for (const k of uncollected) {
        if (load + req.loot[k - 1].weight > Q) continue;
        const reach = dist[cur][k];
        const back = dist[k][0];
        if (spent + tripSteps + reach + back > B) continue;
        const score = mode === "value"
          ? req.loot[k - 1].prize
          : req.loot[k - 1].prize / (reach + 1);
        if (score > pickScore) { pickScore = score; pick = k; }
      }
      if (pick === -1) break;
      tripSteps += dist[cur][pick];
      cur = pick;
      load += req.loot[pick - 1].weight;
      trip.push(pick);
      uncollected.delete(pick);
    }
    if (trip.length === 1) break; // no loot fit in a new trip
    tripSteps += dist[cur][0];
    trip.push(0);
    spent += tripSteps;
    trips.push(trip);
  }

  const { prize, weight, steps, collected } = summarizeTrips(req, trips);
  return { status: "HEURISTIC", prize, weight, steps, trips, collected };
}
